/*
	Sometimes you want to async validate something like name uniqueness before advancing
	to the next step in a wizard.

	Typically, we trigger async validation on blur (so we don't spam requests on change),
	but doing that can lead to difficult timing if you want to disable the button to advance
	while you're awaiting validation, because going from being focused in the field you wish to
	validate, directly to clicking the button to advance will cause the blur to trigger validation,
	and disable the button before it can be clicked, giving the appearance of needing to be
	clicked twice if there's no validation error.

	This hook returns an array with function that can be used with a redux-form form to simplify this use case,
	and a state flag, which is useful for disabling a button, instead of using the asyncValidating flag.

	Essentially, it will monitor the status of your form's async validation, and allow the user to
	advance if async validation ends, and there are no async errors.

	Example usage:
	```
		function WidgetBuilder(){
			function onNext(){
				... // Go to next step in the wizard
			}

			const [attemptAdvance, isAttemptingAdvance] = useAdvanceWizard("WIDGET_FORM", onNext);
			const isFormInvalid = useSelector(isInvalid("WIDGET_FORM"));

			// Instead of disabling when asyncValidating is truthy, we disable when we're attempting to advance,
			// which avoids timing issues on the button click
			const shouldDisableAdvancement = isFormInvalid || isAttemptingAdvance;

			return (
				<div>
					<Field name="name" />
					<Button
						disabled={shouldDisableAdvancement}
						onClick={attemptAdvance}
					/>
				</div>
			);
		}

		async function nameExists(dispatch, name){
			const entity = await dispatch(getEntityWithName(name)); // Whatever action that checks with the API
			if(entity){
				throw { name: "Entity with this name already exists." };
			}
		}

		const WidgetBuilderForm = reduxForm({
			form: "WIDGET_FORM",
			asyncValidate(values, dispatch){
				return nameExists(dispatch, values.name);
			},
			asyncBlurFields: ["name"],
		})(WidgetBuilder);
	```
*/

import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { getFormAsyncErrors } from "redux-form";

export default function useAdvanceWizard(formName, advanceCallback){
	const [isAttemptingAdvance, setIsAttemptingAdvance] = useState(false);

	const asyncValidating = useSelector(state => state.form?.[formName]?.asyncValidating); /*
		redux-form provides `asyncValidating` as a prop to decorated forms, but doesn't expose it
		directly with selectors, so we select for it manually, with the understanding that this is
		unsupported, and may break with redux-form version upgrades, in the interest of simplifying
		the usage of this hook.
	*/
	const asyncErrors = useSelector(getFormAsyncErrors(formName));

	useEffect(() => {
		if(isAttemptingAdvance){
			if(!asyncValidating){
				setIsAttemptingAdvance(false);

				if(!asyncErrors){
					advanceCallback();
				}
			}
		}
	}, [isAttemptingAdvance, asyncValidating, asyncErrors, advanceCallback]);

	function attemptAdvance(){
		setIsAttemptingAdvance(true);
	}

	return [attemptAdvance, isAttemptingAdvance];
}
